// Inline dismissible legend explaining the three Progress Dots gates and their state colours.

import { useState, useCallback } from "react";
import { HelpCircle, X } from "lucide-react";

const STORAGE_KEY = "milestones.dotsKey.dismissed";

function readDismissed(): boolean {
  // Default is dismissed: returning users do not need a persistent legend.
  // First-time users (no stored value) get the dismissed state with a
  // visible "Show progress dots key" affordance to recall the vocabulary.
  if (typeof window === "undefined") return true;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === "0") return false;
    return true;
  } catch {
    return true;
  }
}

function writeDismissed(dismissed: boolean) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, dismissed ? "1" : "0");
  } catch {
    // localStorage is best-effort; private mode or quota errors are ignored.
  }
}

interface SwatchProps {
  className: string;
  label: string;
}

function Swatch({ className, label }: SwatchProps) {
  return (
    <span className="inline-flex items-center gap-1">
      <span
        aria-hidden="true"
        className={`h-2.5 w-2.5 rounded-full border ${className}`}
      />
      <span className="text-caption text-slate-600 dark:text-slate-400">
        {label}
      </span>
    </span>
  );
}

function PartialSwatch() {
  // Half-fill mirrors the actual ProgressDots `pdot.partial` rendering:
  // left half amber, right half slate — communicates "some scope accepted".
  return (
    <span className="inline-flex items-center gap-1">
      <span
        aria-hidden="true"
        className="h-2.5 w-2.5 rounded-full border border-amber-500 bg-linear-to-r from-amber-500 from-50% to-slate-100 to-50% dark:to-slate-700"
      />
      <span className="text-caption text-slate-600 dark:text-slate-400">
        partial
      </span>
    </span>
  );
}

export default function ProgressDotsKey() {
  const [dismissed, setDismissed] = useState<boolean>(readDismissed);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    writeDismissed(true);
  }, []);

  const handleShow = useCallback(() => {
    setDismissed(false);
    writeDismissed(false);
  }, []);

  if (dismissed) {
    return (
      <button
        type="button"
        onClick={handleShow}
        aria-label="Show progress dots key"
        data-testid="progress-dots-key-show"
        className="inline-flex items-center gap-1 rounded-md p-1 text-caption text-slate-500 hover:text-slate-700 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-slate-400 dark:hover:text-slate-200"
      >
        <HelpCircle className="h-3.5 w-3.5" aria-hidden="true" />
        <span>Progress dots key</span>
      </button>
    );
  }

  return (
    <div
      data-testid="progress-dots-key"
      role="note"
      aria-label="Progress dots legend"
      className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800/60"
    >
      <span className="text-caption font-medium text-slate-700 dark:text-slate-200">
        Progress dots: Delivered → Accepted → Paid
      </span>
      <Swatch
        className="border-emerald-500 bg-emerald-500"
        label="on time"
      />
      <Swatch
        className="border-amber-500 bg-amber-500"
        label="delivered late"
      />
      <Swatch
        className="border-red-500 bg-red-500"
        label="late, undelivered"
      />
      <PartialSwatch />
      <Swatch
        className="border-slate-300 bg-slate-100 dark:border-slate-600 dark:bg-slate-800"
        label="not yet"
      />
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss progress dots key"
        data-testid="progress-dots-key-dismiss"
        className="ml-auto inline-flex h-5 w-5 items-center justify-center rounded-md text-slate-500 hover:text-slate-700 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-slate-400 dark:hover:text-slate-200"
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </div>
  );
}
